import { bind } from 'bind-event-listener';

import { getDocument } from '@atlaskit/browser-apis';
import {
	ACTION,
	ACTION_SUBJECT,
	EVENT_TYPE,
	INPUT_METHOD,
	type AnalyticsEventPayload,
	type EditorAnalyticsAPI,
	type InputMethodInsertNativeEmbed,
} from '@atlaskit/editor-common/analytics';
import { getLinkMetadataFromTransaction } from '@atlaskit/editor-common/card';
import {
	NATIVE_EMBED_EXTENSION_KEY,
	NATIVE_EMBED_EXTENSION_TYPE,
} from '@atlaskit/editor-common/extensions';
import { getNestedParentNode } from '@atlaskit/editor-common/node-width';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import { pmHistoryPluginKey, stepHasSlice } from '@atlaskit/editor-common/utils';
import { isReplaceDocOperation } from '@atlaskit/editor-common/utils/document';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import {
	type EditorState,
	NodeSelection,
	PluginKey,
	type ReadonlyTransaction,
	type Transaction,
} from '@atlaskit/editor-prosemirror/state';
import { findSelectedNodeOfType } from '@atlaskit/editor-prosemirror/utils';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import {
	recordRemixRevisionEdit,
	recordRemixRevisionVersion,
} from '@atlaskit/native-embeds-common/remix-slot-store';
import type { AlignmentValue, NativeEmbedParameters } from '@atlaskit/native-embeds-common/types';
import {
	getParameters,
	resolveRemixVersionId,
	setParameter,
} from '@atlaskit/native-embeds-common/utils';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';

import type { SelectedEmbedIdentity } from '../nativeEmbedsPluginType';
import { createDeleteCommand } from './commands/delete';
import { createSetRemixAppIdCommand, getRemixAppId } from './commands/remix-app-id';
import {
	applyRemixEdit,
	getMauiEditRevisionId,
	normalizeRemixVersions,
	readEditorHistoryAction,
	readRemixEdit,
	readRemixVersionCreated,
	REMIX_VERSION_TRANSITION_TRIGGER_META,
	type RemixVersionTransition,
	type RemixVersionTransitionTrigger,
	setRemixVersion,
} from './remix-history';
import { resolveChildResize, type ChildResizePolicy } from './resolve-child-resize';
import { isNativeEmbedNode } from './utils/getSelectedNativeEmbedExtension';
import { isFocusedEmbedVisible } from './utils/is-focused-embed-visible';
import { clearMaxHeight, setMaxHeight } from './utils/max-height-store';

/**
 * Given a candidate document position (already mapped through the latest
 * transaction), returns the position if it still points to a native embed
 * node, or `null` if the embed has been deleted or replaced.
 *
 * Extracted as a named helper so the intent is clear at the call-site and
 * the validation logic can be updated in one place if needed.
 */
const resolveTrackedEmbedPos = (pos: number, newEditorState: EditorState): number | null => {
	const docSize = newEditorState.doc.content.size;
	if (pos >= docSize) {
		return null;
	}
	const nodeAtPos = newEditorState.doc.nodeAt(pos);
	if (!nodeAtPos || !isNativeEmbedNode(nodeAtPos)) {
		return null;
	}
	return pos;
};

const findEventEmbedContainer = (view: EditorView, event: Event): HTMLElement | undefined => {
	const target = event.target;
	if (!(target instanceof HTMLElement)) {
		return undefined;
	}
	let nodeViewDom: HTMLElement | null = target;
	while (
		nodeViewDom &&
		nodeViewDom !== view.dom &&
		!nodeViewDom.classList.contains('extensionView-content-wrap')
	) {
		nodeViewDom = nodeViewDom.parentElement;
	}
	return nodeViewDom && nodeViewDom !== view.dom ? nodeViewDom : undefined;
};

const findNativeEmbedFromEvent = (
	view: EditorView,
	event: Event,
): { dom: HTMLElement; node: PMNode; pos: number } | undefined => {
	const nodeViewDom = findEventEmbedContainer(view, event);
	if (!nodeViewDom) {
		return undefined;
	}
	try {
		const pos = view.posAtDOM(nodeViewDom, 0);
		const $pos = view.state.doc.resolve(pos);
		if ($pos.nodeAfter && isNativeEmbedNode($pos.nodeAfter)) {
			return { node: $pos.nodeAfter, pos, dom: nodeViewDom };
		}
		for (let depth = $pos.depth; depth >= 0; depth--) {
			const node = $pos.node(depth);
			if (isNativeEmbedNode(node)) {
				return { node, pos: $pos.before(depth), dom: nodeViewDom };
			}
		}
	} catch {
		return undefined;
	}
	return undefined;
};

const applyToNativeEmbedFromEvent = (
	view: EditorView,
	event: Event,
	apply: (node: PMNode, pos: number, dom: HTMLElement) => boolean,
): boolean => {
	const embed = findNativeEmbedFromEvent(view, event);
	return embed ? apply(embed.node, embed.pos, embed.dom) : false;
};

const getInsertionMethod = (transactions: readonly Transaction[]): InputMethodInsertNativeEmbed => {
	for (const transaction of transactions) {
		const { inputMethod } = getLinkMetadataFromTransaction(transaction);
		if (inputMethod) {
			return inputMethod as InputMethodInsertNativeEmbed;
		}
	}

	return INPUT_METHOD.MANUAL;
};

export type NativeEmbedsPluginState = {
	/**
	 * The ProseMirror document position of the currently hovered native embed,
	 * kept up-to-date by mapping through every document-changing transaction.
	 * Set by the coordinator when the user moves the pointer over an embed;
	 * cleared when the pointer leaves. Enables the hover overlay to re-measure
	 * when content above the embed shifts (CNS-26923 parity for the hover path)
	 * — even when the embed has never been selected.
	 */
	hoveredEmbedPos: number | null;
	isResizing: boolean;
	/** Effective MAUI version state for each native embed, used to detect accepted pointer changes. */
	remixVersionStateByLocalId: ReadonlyMap<string, RemixVersionState>;
	remixVersionTransitions: ReadonlyMap<string, RemixVersionTransition>;
	resizingWidth?: number;
	selectedEmbed: SelectedEmbedIdentity | null;
	showUrlToolbar: boolean;
	/**
	 * The ProseMirror document position of the most recently selected native
	 * embed, kept up-to-date by mapping through every document-changing
	 * transaction.  Survives deselection so React overlays can re-measure when
	 * content above the embed shifts.  Cleared when the embed is deleted or
	 * replaced with a non-native-embed node.
	 *
	 * Distinct from `SelectedEmbedIdentity.selectionPos`, which is a snapshot
	 * of the position at the moment of selection and is not updated thereafter.
	 */
	trackedEmbedPos: number | null;
};

type NativeEmbedsPluginAction =
	| { type: 'SHOW_URL_TOOLBAR' }
	| { type: 'HIDE_URL_TOOLBAR' }
	| { isResizing: boolean; type: 'SET_RESIZING'; width?: number }
	| { pos: number | null; type: 'SET_HOVERED_EMBED_POS' };

type RemixVersionState = {
	revisionId?: string;
	versionId?: string;
};

const getRemixVersionTransitionTrigger = (
	tr: ReadonlyTransaction | Transaction,
): RemixVersionTransitionTrigger => {
	const explicitTrigger = tr.getMeta(REMIX_VERSION_TRANSITION_TRIGGER_META);
	if (
		explicitTrigger === 'undo' ||
		explicitTrigger === 'redo' ||
		explicitTrigger === 'collab' ||
		explicitTrigger === 'edit_echo' ||
		explicitTrigger === 'host_version_change'
	) {
		return explicitTrigger;
	}

	const historyMeta: unknown = tr.getMeta(pmHistoryPluginKey);
	if (typeof historyMeta === 'object' && historyMeta !== null && 'redo' in historyMeta) {
		const { redo } = historyMeta as { redo?: unknown };
		if (redo === true) {
			return 'redo';
		}
		if (redo === false) {
			return 'undo';
		}
	}

	return tr.getMeta('isRemote') ? 'collab' : 'host_version_change';
};

const indexRemixVersionState = (doc: PMNode): ReadonlyMap<string, RemixVersionState> => {
	const versionStateByLocalId = new Map<string, RemixVersionState>();
	doc.descendants((node) => {
		if (!isNativeEmbedNode(node) || typeof node.attrs.localId !== 'string') {
			return true;
		}
		versionStateByLocalId.set(node.attrs.localId, {
			revisionId: getMauiEditRevisionId(node),
			versionId: resolveRemixVersionId({
				storedRemixAppId: getRemixAppId(node),
				url: getParameters(node.attrs.parameters, 'url'),
			}),
		});
		return false;
	});
	return versionStateByLocalId;
};

const remixVersionStatesAreEqual = (
	left: ReadonlyMap<string, RemixVersionState>,
	right: ReadonlyMap<string, RemixVersionState>,
): boolean => {
	if (left.size !== right.size) {
		return false;
	}
	for (const [localId, leftState] of left) {
		const rightState = right.get(localId);
		if (
			!rightState ||
			leftState.revisionId !== rightState.revisionId ||
			leftState.versionId !== rightState.versionId
		) {
			return false;
		}
	}
	return true;
};

const shouldCreateRemixTransition = ({
	current,
	previous,
	trigger,
}: {
	current: RemixVersionState;
	previous: RemixVersionState;
	trigger: RemixVersionTransitionTrigger;
}): boolean => {
	// A durable version change always needs to be synchronized to the iframe.
	if (previous.versionId !== current.versionId) {
		return true;
	}

	// A forward local edit is already visible in its originating iframe, so
	// sending its revision back would be an echo. Revision-only transitions are
	// meaningful only when undo/redo navigates editor history before minting.
	const isHistoryNavigation = trigger === 'undo' || trigger === 'redo';
	return isHistoryNavigation && previous.revisionId !== current.revisionId;
};

const getRemixStateTransitions = (
	previousVersionState: ReadonlyMap<string, RemixVersionState>,
	currentVersionState: ReadonlyMap<string, RemixVersionState>,
	trigger: RemixVersionTransitionTrigger,
): Omit<RemixVersionTransition, 'applyId' | 'trigger'>[] => {
	const transitions: Omit<RemixVersionTransition, 'applyId' | 'trigger'>[] = [];
	for (const [localId, current] of currentVersionState) {
		const previous = previousVersionState.get(localId);
		if (!current.versionId) {
			continue;
		}
		// Redo can restore an edited embed after undo removed the entire node.
		if (!previous) {
			if (trigger === 'redo' && current.revisionId !== undefined) {
				transitions.push({
					localId,
					revisionId: current.revisionId,
					versionId: current.versionId,
				});
			}
			continue;
		}
		if (!shouldCreateRemixTransition({ current, previous, trigger })) {
			continue;
		}
		transitions.push({
			localId,
			...(current.revisionId === undefined ? {} : { revisionId: current.revisionId }),
			versionId: current.versionId,
		});
	}
	return transitions;
};

export const NATIVE_EMBEDS_IS_RESIZING_KEY = 'nativeEmbedsPlugin.isResizing';

export const pluginKey: PluginKey<NativeEmbedsPluginState> = new PluginKey<NativeEmbedsPluginState>(
	'nativeEmbedsPlugin',
);

/**
 * Walks the document to find all native embed extension nodes and syncs
 * the `data-native-embed-nested` attribute on their DOM `.extension-container`
 * element. This ensures the attribute stays correct after drag-and-drop moves
 * an embed into or out of a nested container.
 *
 * Deferred to a requestAnimationFrame so that the DOM has been fully rebuilt
 * by ProseMirror before we attempt to locate elements.
 */
let syncRAFHandle: number | null = null;
const syncNativeEmbedNestedAttributes = (view: EditorView): void => {
	if (syncRAFHandle !== null) {
		cancelAnimationFrame(syncRAFHandle);
	}
	syncRAFHandle = requestAnimationFrame(() => {
		syncRAFHandle = null;
		if (view.isDestroyed) {
			return;
		}
		const { state } = view;
		state.doc.descendants((node, pos) => {
			if (!isNativeEmbedNode(node)) {
				return true; // continue traversal
			}

			try {
				// Use view.nodeDOM to get the actual node's DOM element, bypassing
				// decorations (like gap cursors) that share the same position.
				const rawDom = view.nodeDOM(pos);
				const element =
					rawDom instanceof HTMLElement
						? rawDom
						: rawDom instanceof Node
							? (rawDom.parentElement as HTMLElement | null)
							: null;
				if (!element) {
					return false;
				}

				const container =
					(element.querySelector('.extension-container') as HTMLElement | null) ??
					(element.closest('.extension-container') as HTMLElement | null) ??
					element;

				if (getNestedParentNode(pos, state) !== null) {
					container.setAttribute('data-native-embed-nested', '');
				} else {
					container.removeAttribute('data-native-embed-nested');
				}
			} catch {
				// DOM may not be available for this position yet — skip
			}

			return false; // no need to descend into extension node
		});
	});
};

export const createPlugin = (
	editorAnalyticsApi?: EditorAnalyticsAPI,
	undoRedoActions?: { redo: () => boolean; undo: () => boolean },
): SafePlugin<NativeEmbedsPluginState> => {
	const writtenMaxHeightLocalIds = new Set<string>();

	// Experiment value is stable for the lifetime of the plugin, so resolve it
	// once at construction time. Avoids redundant flag lookups (and exposure
	// logs) on every child resize event.
	const childResizePolicy: ChildResizePolicy = {
		gateWidth: true,
		gateHeight:
			expValEqualsNoExposure('cc-maui-experiment', 'isEnabled', true) &&
			expValEquals('databases-native-embeds-v2', 'isEnabled', true),
	};

	return new SafePlugin<NativeEmbedsPluginState>({
		key: pluginKey,
		props: {
			handleDOMEvents: {
				// Handle click-inside events dispatched by the native embed iframe
				// extension component. We create a proper ProseMirror NodeSelection
				// so that keyboard interactions (Delete, Backspace) work as expected.
				'native-embed-click-inside': (view, event) => {
					const target = event.target;
					if (!(target instanceof HTMLElement)) {
						return false;
					}

					// Walk up the DOM from the event target to find the node view
					// wrapper element (identified by the extensionView-content-wrap
					// class added by ReactNodeView). This element has a pmViewDesc
					// that ProseMirror can resolve to a document position.
					let nodeViewDom: HTMLElement | null = target;
					while (
						nodeViewDom &&
						nodeViewDom !== view.dom &&
						!nodeViewDom.classList.contains('extensionView-content-wrap')
					) {
						nodeViewDom = nodeViewDom.parentElement;
					}

					if (!nodeViewDom || nodeViewDom === view.dom) {
						return false;
					}

					try {
						const pos = view.posAtDOM(nodeViewDom, 0);
						const $pos = view.state.doc.resolve(pos);

						// For atom nodes (like extension), posAtDOM on the node
						// view wrapper resolves to the position of the node.
						// Check nodeAfter at this position.
						const nodeAfter = $pos.nodeAfter;
						if (
							nodeAfter &&
							nodeAfter.type.name === 'extension' &&
							nodeAfter.attrs?.extensionType === NATIVE_EMBED_EXTENSION_TYPE &&
							nodeAfter.attrs?.extensionKey?.includes(NATIVE_EMBED_EXTENSION_KEY)
						) {
							const tr = view.state.tr.setSelection(NodeSelection.create(view.state.doc, pos));
							view.dispatch(tr);
							if ((event as CustomEvent).detail?.shouldFocus !== false) {
								view.focus();
							}
							return true;
						}

						// Fallback: walk up the ancestor chain in case the position
						// resolved inside a wrapper node.
						for (let d = $pos.depth; d >= 0; d--) {
							const node = $pos.node(d);
							if (
								node.type.name === 'extension' &&
								node.attrs?.extensionType === NATIVE_EMBED_EXTENSION_TYPE &&
								node.attrs?.extensionKey?.includes(NATIVE_EMBED_EXTENSION_KEY)
							) {
								const nodePos = $pos.before(d);
								const tr = view.state.tr.setSelection(
									NodeSelection.create(view.state.doc, nodePos),
								);
								view.dispatch(tr);
								if ((event as CustomEvent).detail?.shouldFocus !== false) {
									view.focus();
								}
								return true;
							}
						}
					} catch {
						// posAtDOM can throw if the DOM element is not within the editor
					}
					return false;
				},
				'native-embed-request-resize': (view, event) => {
					const detail = (event as CustomEvent).detail;
					const newWidth = typeof detail?.width === 'number' ? detail.width : undefined;
					const newHeight = typeof detail?.height === 'number' ? detail.height : undefined;
					const newMaxHeight =
						typeof detail?.maxHeight === 'number' && Number.isFinite(detail.maxHeight)
							? detail.maxHeight
							: undefined;
					const shouldPersistResize = newWidth !== undefined || newHeight !== undefined;
					const target = event.target;
					if (
						!(target instanceof HTMLElement) ||
						(newWidth === undefined && newHeight === undefined && newMaxHeight === undefined)
					) {
						return false;
					}

					let nodeViewDom: HTMLElement | null = target;
					while (
						nodeViewDom &&
						nodeViewDom !== view.dom &&
						!nodeViewDom.classList.contains('extensionView-content-wrap')
					) {
						nodeViewDom = nodeViewDom.parentElement;
					}

					if (!nodeViewDom || nodeViewDom === view.dom) {
						return false;
					}

					// Width is always gated — once the host has persisted a
					// width, further child width requests are dropped. Height
					// is gated behind `databases-native-embeds-v2` while the
					// child-driven vertical-resize capability rolls out; in both cases
					// the rule is "lock the axis on the first child-driven
					// resize, ignore further child requests on that axis". See
					// `resolve-child-resize.ts` for the pure policy fn; the
					// policy itself is built once in `createPlugin` above.
					const buildUpdatedParams = (params: NativeEmbedParameters | undefined) => {
						const { width: applyWidth, height: applyHeight } = resolveChildResize(
							params,
							{ width: newWidth, height: newHeight },
							childResizePolicy,
						);
						let updated = params ?? {};
						if (applyWidth !== undefined) {
							updated = setParameter(updated, { width: applyWidth });
						}
						if (applyHeight !== undefined) {
							updated = setParameter(updated, { height: applyHeight });
						}
						return updated;
					};

					try {
						const pos = view.posAtDOM(nodeViewDom, 0);
						const $pos = view.state.doc.resolve(pos);

						const nodeAfter = $pos.nodeAfter;
						if (
							nodeAfter &&
							nodeAfter.type.name === 'extension' &&
							nodeAfter.attrs?.extensionType === NATIVE_EMBED_EXTENSION_TYPE &&
							nodeAfter.attrs?.extensionKey?.includes(NATIVE_EMBED_EXTENSION_KEY)
						) {
							const localId = nodeAfter.attrs?.localId;
							if (
								typeof localId === 'string' &&
								localId !== '' &&
								newMaxHeight !== undefined &&
								newMaxHeight > 0
							) {
								setMaxHeight(view, localId, newMaxHeight);
								writtenMaxHeightLocalIds.add(localId);
							}
							if (!shouldPersistResize) {
								return true;
							}
							const updatedParams = buildUpdatedParams(
								nodeAfter.attrs.parameters as NativeEmbedParameters | undefined,
							);
							const tr = view.state.tr.setNodeMarkup(pos, undefined, {
								...nodeAfter.attrs,
								parameters: updatedParams,
							});
							view.dispatch(tr);
							return true;
						}

						for (let d = $pos.depth; d >= 0; d--) {
							const node = $pos.node(d);
							if (
								node.type.name === 'extension' &&
								node.attrs?.extensionType === NATIVE_EMBED_EXTENSION_TYPE &&
								node.attrs?.extensionKey?.includes(NATIVE_EMBED_EXTENSION_KEY)
							) {
								const localId = node.attrs?.localId;
								if (
									typeof localId === 'string' &&
									localId !== '' &&
									newMaxHeight !== undefined &&
									newMaxHeight > 0
								) {
									setMaxHeight(view, localId, newMaxHeight);
									writtenMaxHeightLocalIds.add(localId);
								}
								if (!shouldPersistResize) {
									return true;
								}
								const nodePos = $pos.before(d);
								const updatedParams = buildUpdatedParams(
									node.attrs.parameters as NativeEmbedParameters | undefined,
								);
								const tr = view.state.tr.setNodeMarkup(nodePos, undefined, {
									...node.attrs,
									parameters: updatedParams,
								});
								view.dispatch(tr);
								return true;
							}
						}
					} catch {
						// posAtDOM can throw if the DOM element is not within the editor
					}
					return false;
				},
				'native-embed-new-version': (view, event) => {
					if (!isExperimentEnabled('cc_maui_editing_experience')) {
						return false;
					}
					const detail =
						event instanceof CustomEvent ? readRemixVersionCreated(event.detail) : undefined;
					if (!detail) {
						return false;
					}
					return applyToNativeEmbedFromEvent(view, event, (embedNode, nodePos) => {
						const currentVersionId = resolveRemixVersionId({
							storedRemixAppId: getRemixAppId(embedNode),
							url: getParameters(embedNode.attrs.parameters, 'url'),
						});
						if (detail.revisionId) {
							recordRemixRevisionVersion(
								embedNode.attrs.localId,
								detail.revisionId,
								detail.remixAppId,
							);
						}
						if (currentVersionId === detail.remixAppId) {
							return true;
						}
						if (detail.revisionId) {
							if (getMauiEditRevisionId(embedNode) !== detail.revisionId) {
								return true;
							}
							view.dispatch(
								setRemixVersion(view.state.tr, embedNode, nodePos, detail.remixAppId).setMeta(
									REMIX_VERSION_TRANSITION_TRIGGER_META,
									'edit_echo',
								),
							);
							return true;
						}
						return createSetRemixAppIdCommand(
							detail.remixAppId,
							nodePos,
							embedNode,
							'edit_echo',
						)(view.state, view.dispatch);
					});
				},
				'native-embed-remix-edit': (view, event) => {
					if (!isExperimentEnabled('cc_maui_editing_experience')) {
						return false;
					}
					const edit = event instanceof CustomEvent ? readRemixEdit(event.detail) : undefined;
					if (!edit) {
						return false;
					}
					return applyToNativeEmbedFromEvent(view, event, (embedNode, nodePos, embedDom) => {
						const currentVersionId = resolveRemixVersionId({
							storedRemixAppId: getRemixAppId(embedNode),
							url: getParameters(embedNode.attrs.parameters, 'url'),
						});
						recordRemixRevisionEdit({
							currentVersionId,
							localId: embedNode.attrs.localId,
							previousRevisionId: getMauiEditRevisionId(embedNode),
							previousValue: edit.previousValue,
							revisionId: edit.revisionId,
							slot: edit.slot,
							value: edit.value,
						});
						const tr = applyRemixEdit(view.state.tr, embedNode, nodePos, edit);
						if (!tr) {
							return false;
						}
						if (isFocusedEmbedVisible(embedDom)) {
							tr.setMeta('scrollIntoView', false);
						}
						view.dispatch(tr);
						return true;
					});
				},
				// Gated: the embedded app forwarded a Cmd/Ctrl+Z undo/redo intent
				// (its editable text suppressed the browser's native contentEditable undo).
				// Run the editor's undo/redo so it is the single history authority that drives
				// the remix version pointer, whether focus is in the page or inside the app.
				'native-embed-editor-history': (view, event) => {
					if (!isExperimentEnabled('cc_maui_editing_experience')) {
						return false;
					}
					const action =
						event instanceof CustomEvent ? readEditorHistoryAction(event.detail) : undefined;
					if (action !== 'undo' && action !== 'redo') {
						return false;
					}
					// The intent originated inside the embed iframe, so move focus to the editor
					// before running the command.
					view.focus();
					return undoRedoActions?.[action]() ?? false;
				},
				// Handle delete requests from child embeds via the bridge.
				// The child sends { type: 'delete' } when the user presses
				// Delete/Backspace without a focused text input inside the
				// iframe. Only MAUI embeds are eligible for bridge-initiated
				// deletion (verified via the /remix/ui/ URL pattern).
				'native-embed-request-delete': (view) => {
					const selected = findSelectedNodeOfType(view.state.schema.nodes.extension)(
						view.state.selection,
					);
					if (selected && isNativeEmbedNode(selected.node)) {
						const deleted = createDeleteCommand(selected)(view.state, view.dispatch);
						if (deleted) {
							// After bridge-initiated deletion, transfer focus back to the
							// editor so subsequent keyboard shortcuts (e.g. Ctrl+Z to undo)
							// are handled by ProseMirror rather than being consumed by the
							// now-removed iframe.
							view.focus();
						}
						return deleted;
					}
					return false;
				},
			},
		},
		view: (editorView: EditorView) => {
			// Run an initial sync so embeds that are already nested when the
			// editor first mounts get the correct attribute immediately.
			syncNativeEmbedNestedAttributes(editorView);

			// In view mode (editorView.editable === false), clicks on a native embed
			// node don't naturally select it via ProseMirror's event handling.
			// This listener intercepts mousedown events at the document level and
			// manually dispatches a NodeSelection for the clicked embed, so that
			// the floating toolbar appears as expected in view/live-page mode.
			const handleDocMouseDown = (event: MouseEvent) => {
				if (editorView.editable) return;
				const target = event.target;
				if (!(target instanceof HTMLElement)) return;
				const wrapper = target.closest('.extensionView-content-wrap');
				if (!wrapper || !editorView.dom.contains(wrapper)) return;
				try {
					const pos = editorView.posAtDOM(wrapper, 0);
					const nodeAfter = editorView.state.doc.resolve(pos).nodeAfter;
					if (nodeAfter?.type.name === 'extension') {
						const tr = editorView.state.tr.setSelection(
							NodeSelection.create(editorView.state.doc, pos),
						);
						editorView.dispatch(tr);
					}
				} catch {
					// pos stale
				}
			};
			const doc = getDocument();
			const unbindMouseDown = doc
				? bind(doc, { type: 'mousedown', listener: handleDocMouseDown, options: { capture: true } })
				: () => {};

			return {
				update: (view: EditorView, prevState: EditorState) => {
					if (prevState.doc !== view.state.doc) {
						syncNativeEmbedNestedAttributes(view);
						const liveLocalIds = new Set<string>();
						view.state.doc.descendants((node) => {
							if (isNativeEmbedNode(node) && typeof node.attrs.localId === 'string') {
								liveLocalIds.add(node.attrs.localId);
							}
							return true;
						});
						for (const localId of writtenMaxHeightLocalIds) {
							if (!liveLocalIds.has(localId)) {
								clearMaxHeight(editorView, localId);
								writtenMaxHeightLocalIds.delete(localId);
							}
						}
					}
				},
				destroy: () => {
					unbindMouseDown();
					if (syncRAFHandle !== null) {
						cancelAnimationFrame(syncRAFHandle);
						syncRAFHandle = null;
					}
					for (const localId of writtenMaxHeightLocalIds) {
						clearMaxHeight(editorView, localId);
					}
					writtenMaxHeightLocalIds.clear();
				},
			};
		},
		state: {
			init: (_config, editorState): NativeEmbedsPluginState => ({
				isResizing: false,
				remixVersionStateByLocalId: isExperimentEnabled('cc_maui_editing_experience')
					? indexRemixVersionState(editorState.doc)
					: new Map<string, RemixVersionState>(),
				remixVersionTransitions: new Map(),
				trackedEmbedPos: null,
				hoveredEmbedPos: null,
				selectedEmbed: null,
				showUrlToolbar: false,
			}),
			apply: (tr, state, _oldEditorState, newEditorState): NativeEmbedsPluginState => {
				const meta = tr.getMeta(pluginKey) as NativeEmbedsPluginAction | undefined;
				let nextState = state;

				if (tr.docChanged && isExperimentEnabled('cc_maui_editing_experience')) {
					const trigger = getRemixVersionTransitionTrigger(tr);
					const indexedRemixVersionState = indexRemixVersionState(newEditorState.doc);
					const remixVersionStateByLocalId = remixVersionStatesAreEqual(
						state.remixVersionStateByLocalId,
						indexedRemixVersionState,
					)
						? state.remixVersionStateByLocalId
						: indexedRemixVersionState;
					const remixTransitions = getRemixStateTransitions(
						state.remixVersionStateByLocalId,
						remixVersionStateByLocalId,
						trigger,
					);
					let remixVersionTransitions = nextState.remixVersionTransitions;
					if (remixTransitions.length > 0) {
						const updatedTransitions = new Map(remixVersionTransitions);
						for (const transition of remixTransitions) {
							updatedTransitions.set(transition.localId, {
								...transition,
								applyId: crypto.randomUUID(),
								trigger,
							});
						}
						remixVersionTransitions = updatedTransitions;
					}

					if (remixVersionTransitions.size > 0) {
						let updatedTransitions: Map<string, RemixVersionTransition> | undefined;
						for (const localId of remixVersionTransitions.keys()) {
							if (!remixVersionStateByLocalId.has(localId)) {
								updatedTransitions ??= new Map(remixVersionTransitions);
								updatedTransitions.delete(localId);
							}
						}
						if (updatedTransitions) {
							remixVersionTransitions = updatedTransitions;
						}
					}
					if (
						remixVersionStateByLocalId !== state.remixVersionStateByLocalId ||
						remixVersionTransitions !== state.remixVersionTransitions
					) {
						nextState = {
							...nextState,
							remixVersionStateByLocalId,
							remixVersionTransitions,
						};
					}
				}

				// Map the last-known embed positions through document changes
				// (following the card/paste/extension plugin pattern).
				// When content above an embed is inserted or removed, the
				// mapped pos shifts — producing a new shared state that
				// triggers onChange in React overlays.
				if (tr.docChanged) {
					if (nextState.trackedEmbedPos !== null) {
						const mappedPos = tr.mapping.map(nextState.trackedEmbedPos);
						const resolvedPos = resolveTrackedEmbedPos(mappedPos, newEditorState);
						if (resolvedPos !== nextState.trackedEmbedPos) {
							nextState = { ...nextState, trackedEmbedPos: resolvedPos };
						}
					}
					if (nextState.hoveredEmbedPos !== null) {
						const mappedPos = tr.mapping.map(nextState.hoveredEmbedPos);
						const resolvedPos = resolveTrackedEmbedPos(mappedPos, newEditorState);
						if (resolvedPos !== nextState.hoveredEmbedPos) {
							nextState = { ...nextState, hoveredEmbedPos: resolvedPos };
						}
					}
				}

				if (meta?.type === 'SET_HOVERED_EMBED_POS') {
					// Defensively map `meta.pos` through this transaction's mapping
					// before storing. The coordinator computes the pos against the
					// PRE-dispatch view.state, but if a concurrent input rule or
					// other appendTransaction has rewritten the doc in this same
					// transaction, the raw pos can be off by N. Mapping then
					// resolving keeps the stored hoveredEmbedPos consistent with
					// the doc that this `apply()` is producing.
					if (meta.pos === null) {
						nextState = { ...nextState, hoveredEmbedPos: null };
					} else {
						const mappedPos = tr.docChanged ? tr.mapping.map(meta.pos) : meta.pos;
						const resolvedPos = resolveTrackedEmbedPos(mappedPos, newEditorState);
						nextState = { ...nextState, hoveredEmbedPos: resolvedPos };
					}
				}

				if (meta?.type === 'SHOW_URL_TOOLBAR') {
					nextState = { ...nextState, showUrlToolbar: true };
				}
				if (meta?.type === 'HIDE_URL_TOOLBAR') {
					nextState = { ...nextState, showUrlToolbar: false };
				}
				if (meta?.type === 'SET_RESIZING') {
					nextState = {
						...nextState,
						isResizing: meta.isResizing,
						resizingWidth: meta.width,
					};
				}
				const { schema, selection } = newEditorState;
				const selected = findSelectedNodeOfType(schema.nodes.extension)(selection);
				const isNativeEmbed = selected && isNativeEmbedNode(selected.node);

				if (isNativeEmbed) {
					const newPos = selected.pos;
					const newAlignment = getParameters(
						selected.node.attrs.parameters,
						'alignment',
					) as AlignmentValue;
					const newLocalId: string = selected.node.attrs.localId;
					const prev = nextState.selectedEmbed;

					if (
						newPos !== prev?.selectionPos ||
						newAlignment !== prev?.alignment ||
						newLocalId !== prev?.localId
					) {
						nextState = {
							...nextState,
							trackedEmbedPos: newPos,
							selectedEmbed: { selectionPos: newPos, alignment: newAlignment, localId: newLocalId },
						};
					}
				} else if (nextState.selectedEmbed !== null) {
					nextState = {
						...nextState,
						selectedEmbed: null,
					};
				}

				return nextState;
			},
		},
		appendTransaction: (transactions, oldState, newState) => {
			if (isExperimentEnabled('cc_maui_editing_experience')) {
				const normalized = normalizeRemixVersions(newState.tr);
				if (normalized) {
					const trigger =
						transactions
							.map(getRemixVersionTransitionTrigger)
							.find((candidate) => candidate !== 'host_version_change') ?? 'host_version_change';
					return normalized.setMeta(REMIX_VERSION_TRANSITION_TRIGGER_META, trigger);
				}
			}
			if (!editorAnalyticsApi) {
				return null;
			}

			if (isReplaceDocOperation(transactions, oldState)) {
				return null;
			}

			const isNotLocalInsert = transactions.some((t) => {
				const action = getLinkMetadataFromTransaction(t).action;
				const isRemote = t.getMeta('isRemote');
				return isRemote || !(action === ACTION.INSERTED || action === undefined);
			});

			if (isNotLocalInsert) {
				return null;
			}

			const existingLocalIds = new Set<string>();
			oldState.doc.descendants((node) => {
				if (isNativeEmbedNode(node) && node.attrs.localId) {
					existingLocalIds.add(node.attrs.localId);
				}
			});

			const insertedEmbeds = transactions.flatMap((tr) => {
				const inserted: PMNode[] = [];

				if (!tr.docChanged) {
					return inserted;
				}

				tr.steps.forEach((step) => {
					if (stepHasSlice(step)) {
						step.slice.content.descendants((node: PMNode) => {
							if (
								isNativeEmbedNode(node) &&
								node.attrs.localId !== null &&
								!existingLocalIds.has(node.attrs.localId)
							) {
								inserted.push(node);
							}
						});
					}
				});

				return inserted;
			});

			if (insertedEmbeds.length === 0) {
				return null;
			}

			const tr = newState.tr;

			const payload: AnalyticsEventPayload = {
				action: ACTION.INSERTED,
				actionSubject: ACTION_SUBJECT.NATIVE_EMBED,
				eventType: EVENT_TYPE.TRACK,
				attributes: {
					insertedCount: insertedEmbeds.length,
					inputMethod: getInsertionMethod(transactions),
				},
			};

			editorAnalyticsApi.attachAnalyticsEvent(payload)(tr);

			return tr;
		},
	});
};
